<script setup lang="ts">
import type {
  PhotoGalleryDetailDTO,
  PhotoGalleryDTO,
} from '~/shared/api/client/galleries/galleries.dto';
import type { PhotoDTO } from '~/shared/api/client/photo/photo.dto';
import type { MediaItem } from '~/shared/ui/adaptive-media-grid/types';
import AdaptiveMediaGrid from '~/shared/ui/adaptive-media-grid/AdaptiveMediaGrid.vue';
import SliderModalGallery from '~/shared/ui/slider/SliderModalGallery.vue';
import { EVENT_MEDIA_MODES } from '~/shared/lib/events-routes.const';
import EventsGalleryDetailLayout from './EventsGalleryDetailLayout.vue';
import { useEventGalleryDetail } from '../lib/useEventGalleryDetail';
import { useGalleryModal } from '../lib/useGalleryModal';

const {
  config,
  gallery,
  hasGallery,
  isPending,
  pageUrl,
  relatedGalleries,
  title,
} = useEventGalleryDetail<PhotoGalleryDetailDTO, PhotoGalleryDTO>(
  EVENT_MEDIA_MODES.photos,
);

const galleryPhotos = computed(() =>
  (gallery.value?.photos ?? []).filter((photo) =>
    Boolean(photo.photo?.file_url),
  ),
);

const mediaItems = computed<MediaItem<PhotoDTO>[]>(() =>
  galleryPhotos.value.map((photo) => ({
    id: photo.id,
    src: photo.photo.file_url,
    alt: photo.description || title.value || 'Фото мероприятия',
    payload: photo,
  })),
);

const { $viewport } = useNuxtApp();
const isMobile = computed(() => $viewport.isLessOrEquals('xs'));
const { isOpen, startIndex, open, close } = useGalleryModal();
</script>

<template>
  <div :class="$style.root">
    <EventsGalleryDetailLayout
      :mode="EVENT_MEDIA_MODES.photos"
      :title="title"
      :page-url="pageUrl"
      :is-pending="isPending"
      :has-gallery="hasGallery"
      :related-galleries="relatedGalleries"
    >
      <AdaptiveMediaGrid
        v-if="mediaItems.length > 0"
        :items="mediaItems"
        @item-click="(_, index) => open(index)"
      />

      <div v-else :class="$style.emptyState">
        <span :class="$style.stateText">{{ config.emptyText }}</span>
      </div>
    </EventsGalleryDetailLayout>

    <Modal
      v-if="galleryPhotos.length > 0"
      :is-visible="isOpen"
      title="Фотогалерея"
      @close="close"
    >
      <SliderModalGallery
        :photos="galleryPhotos"
        :start-index="startIndex"
        :is-open="isOpen"
        :is-mobile="isMobile"
      />
    </Modal>
  </div>
</template>

<style module lang="scss">
.root {
  min-height: 100%;
}

.emptyState {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 240px;
}

.stateText {
  @include text_body1;
  margin: 0;
  color: var(--color-gray-text);
}
</style>
